import { useEffect, useRef } from "react";

// The keyboard answers a confirmation modal takes while it is up: Escape
// cancels, Enter confirms — the macOS default-button feel the modals'
// button order already follows. Enter stands down when a control has the
// focus (a focused Cancel or a menu answers Enter itself; a field or a
// text area keeps its own Enter), while it is held down (auto-repeat),
// mid-IME composition, or once something closer to the key has handled it.

const INTERACTIVE =
  'button, a[href], input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="button"], [role="menuitem"], [role="option"], [role="combobox"], [role="listbox"], [role="tab"]';

/** Whether this Enter stands down instead of reaching the modal. */
function enterStandsDown(e: KeyboardEvent): boolean {
  if (e.defaultPrevented) return true;
  return e.repeat || e.isComposing;
}

/** Whether this Enter should run the modal's confirm action. */
export function enterConfirms(e: KeyboardEvent): boolean {
  if (e.key !== "Enter") return false;
  if (enterStandsDown(e)) return false;
  const target = e.target;
  return !(target instanceof Element && target.closest(INTERACTIVE) !== null);
}

/**
 * Escape → onCancel, Enter → onConfirm, document-wide while `active`. The
 * handlers are read at key time, so callers may pass fresh closures each
 * render without re-binding the listener; pass `active: false` while the
 * modal is busy to hold both keys.
 */
export function useModalKeys(options: {
  active: boolean;
  onCancel: () => void;
  onConfirm?: () => void;
}) {
  const { active } = options;
  const latest = useRef(options);
  latest.current = options;
  useEffect(() => {
    if (!active) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        latest.current.onCancel();
        return;
      }
      if (enterConfirms(e)) latest.current.onConfirm?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [active]);
}
